import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Divider,
  Stack,
  Card,
  CardContent,
  Link
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot
} from '@mui/lab';
import { Download } from '@mui/icons-material';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';
import ChangeRequestDetailsView from './ChangeRequestDetailsView';

const PublicFeedbackView = () => {
  const { token } = useParams();
  const { showToast } = useToast();
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFeedback();
  }, [token]);

  const fetchFeedback = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/v1/feedback/public/${token}`);
      setFeedback(response.data);
    } catch (error) {
      console.error('Error fetching feedback:', error);
      showToast('Failed to load feedback', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'New': 'grey',
      'In Progress': 'info',
      'Fixed': 'success',
      'Closed': 'grey',
      'Reopened': 'warning',
      'On Hold': 'warning'
    };
    return colors[status] || 'grey';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'Blocker': 'error',
      'Critical': 'error',
      'High': 'warning',
      'Medium': 'info',
      'Low': 'default'
    };
    return colors[priority] || 'default';
  };

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  if (!feedback) {
    return <Typography>Feedback not found</Typography>;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Feedback Details - {feedback.feedbackId}
      </Typography>

      <Grid container spacing={3}>
        {/* Main Details */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {feedback.feedbackId}
            </Typography>
            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Type
                </Typography>
                <Typography variant="body1">{feedback.type}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Priority
                </Typography>
                <Chip
                  label={feedback.priority}
                  color={getPriorityColor(feedback.priority)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Severity
                </Typography>
                <Typography variant="body1">{feedback.severity}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Status
                </Typography>
                <Chip
                  label={feedback.status}
                  color={getStatusColor(feedback.status)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                {feedback.changeRequestDetails ? (
                  <ChangeRequestDetailsView details={feedback.changeRequestDetails} />
                ) : (
                  <>
                    <Typography variant="subtitle2" color="textSecondary">
                      Description
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                      {feedback.description}
                    </Typography>
                  </>
                )}
              </Grid>
            </Grid>

            {/* Voice Recording */}
            {feedback.voiceRecordingUrl && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Voice Recording
                </Typography>
                <audio 
                  controls 
                  src={
                    feedback.voiceRecordingUrl.startsWith('http') 
                      ? feedback.voiceRecordingUrl 
                      : `${window.location.origin}${feedback.voiceRecordingUrl.startsWith('/') ? '' : '/'}${feedback.voiceRecordingUrl}`
                  } 
                  style={{ width: '100%', maxWidth: '500px' }} 
                />
              </Box>
            )}

            {/* Attachments */}
            {feedback.attachments && feedback.attachments.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Attachments
                </Typography>
                <Stack spacing={1}>
                  {feedback.attachments.map((attachment, index) => {
                    const attachmentUrl = attachment.url.startsWith('http') 
                      ? attachment.url 
                      : `${window.location.origin}${attachment.url.startsWith('/') ? '' : '/'}${attachment.url}`;
                    return (
                      <Link
                        key={index}
                        href={attachmentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Download />
                        {attachment.filename} ({(attachment.size / 1024).toFixed(1)} KB)
                      </Link>
                    );
                  })}
                </Stack>
              </Box>
            )}

            {/* Screenshot */}
            {feedback.screenshotUrl && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Screenshot
                </Typography>
                <img
                  src={
                    feedback.screenshotUrl.startsWith('http') 
                      ? feedback.screenshotUrl 
                      : `${window.location.origin}${feedback.screenshotUrl.startsWith('/') ? '' : '/'}${feedback.screenshotUrl}`
                  }
                  alt="Screenshot"
                  style={{ maxWidth: '100%', border: '1px solid #ccc', borderRadius: '4px' }}
                />
              </Box>
            )}
          </Paper>

          {/* Comments Section (Read-only) */}
          {feedback.comments && feedback.comments.length > 0 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Comments
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                {feedback.comments.map((comment, index) => (
                  <Card key={index} variant="outlined">
                    <CardContent>
                      <Typography variant="body2" gutterBottom>
                        {comment.comment}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {comment.commentedBy
                          ? `${comment.commentedBy.firstName} ${comment.commentedBy.lastName}`
                          : 'Unknown'}{' '}
                        - {new Date(comment.commentedAt).toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Paper>
          )}
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Status Info */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Status Information
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">
                  Status
                </Typography>
                <Typography variant="body1">{feedback.status}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">
                  Component
                </Typography>
                <Typography variant="body1">{feedback.component || 'N/A'}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">
                  Environment
                </Typography>
                <Typography variant="body1">{feedback.environment || 'N/A'}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">
                  Version
                </Typography>
                <Typography variant="body1">{feedback.version || 'N/A'}</Typography>
              </Box>
            </Stack>
          </Paper>

          {/* Status History */}
          {feedback.statusHistory && feedback.statusHistory.length > 0 && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Status History
              </Typography>
              <Timeline>
                {feedback.statusHistory.map((history, index) => (
                  <TimelineItem key={index}>
                    <TimelineSeparator>
                      <TimelineDot color={getStatusColor(history.status)} />
                      {index < feedback.statusHistory.length - 1 && <TimelineConnector />}
                    </TimelineSeparator>
                    <TimelineContent>
                      <Typography variant="subtitle2">
                        {history.status}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {history.changedBy
                          ? `${history.changedBy.firstName} ${history.changedBy.lastName}`
                          : 'System'}{' '}
                        - {new Date(history.changedAt).toLocaleString()}
                      </Typography>
                      {history.notes && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {history.notes}
                        </Typography>
                      )}
                    </TimelineContent>
                  </TimelineItem>
                ))}
              </Timeline>
            </Paper>
          )}

          {/* Submitted Info */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Submitted By
            </Typography>
            <Typography variant="body2">
              {feedback.submittedBy
                ? `${feedback.submittedBy.firstName} ${feedback.submittedBy.lastName}`
                : 'Anonymous'}
            </Typography>
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              {new Date(feedback.createdAt).toLocaleString()}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PublicFeedbackView;

